import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { Send, Landmark, Car, IndianRupee, Truck, Plus, Quote, Star, ChevronDown, CheckCircle2, Home, Gem, ArrowRight, ClipboardList, Scale, BadgeCheck, ArrowDown, Gift, Coins, ShoppingBag, Receipt, Sparkles, ArrowLeftRight, SlidersHorizontal, HeartPulse, Activity, Check, Layers, Percent, PhoneCall, RotateCcw, Wallet } from "lucide-react";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, query, where, orderBy } from "firebase/firestore";
import ContactResponseModal from "../ContactUs/ContactResponseModal";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import indiaCitiesData from "./indiaCarLoanCities.json";
import p2bLogo from "../../assets/p2b_logo.webp";
import appStoreBadge from "../../assets/appStoreButton.svg";
import playStoreBadge from "../../assets/playStoreButton.svg";
import appScreenBenefits from "../../assets/app-screens/benefits.webp";
import appScreenLoans from "../../assets/app-screens/loans.webp";
import { APPSTORE_URL, PLAYSTORE_URL } from "../../constants/Constants";
import heroLoanCarImg from "../../assets/loan-types/hero/car.webp";
import heroLoanHomeImg from "../../assets/loan-types/hero/home.webp";
import heroLoanCommercialImg from "../../assets/loan-types/hero/commercial.webp";
import heroLoanPersonalImg from "../../assets/loan-types/hero/personal.webp";
import heroLoanGoldImg from "../../assets/loan-types/hero/gold.webp";
import heroLoanBusinessImg from "../../assets/loan-types/hero/business.webp";
import heroLoanTravelImg from "../../assets/loan-types/hero/travel.webp";
// Loan-type tile images — small (≤240px) webp cut-outs made from existing
// site artwork, in src/assets/loan-types/. Swap any file to change a tile.
import loanTypeCarImg from "../../assets/loan-types/car.webp";
import loanTypeHomeImg from "../../assets/loan-types/home.webp";
import loanTypeCommercialImg from "../../assets/loan-types/commercial.webp";
import loanTypePersonalImg from "../../assets/loan-types/personal.webp";
import loanTypeGoldImg from "../../assets/loan-types/gold.webp";
import loanTypeBusinessImg from "../../assets/loan-types/business.webp";
import loanTypeTravelImg from "../../assets/loan-types/travel.webp";
import customerLoanImage1 from "../../assets/customerLoanImage1.webp";
import customerLoanImage2 from "../../assets/customerLoanImage2.webp";
import customerLoanImage3 from "../../assets/customerLoanImage3.webp";
import customerLoanImage4 from "../../assets/customerLoanImage4.webp";

/* ─────────────────────────────────────────────────────────────
   CarLoanApply — the destination behind every car-loan "Apply
   Now" CTA (route: /car-loan/apply). Styled as a lead-gen landing
   page (photo hero + floating form card, benefits strip, lender
   logos, 3-step process) after a Park+ car-loan page reference.

   Deliberately NOT included: a "Happy Customers" testimonials
   section like the reference has. Those would need real named
   customers, photos and quotes — inventing people and reviews to
   present as genuine feedback on a real financial product isn't
   something to fabricate, reference page or not.

   Same Formik + Yup + Firestore pattern as ContactUs.jsx for the
   form itself: no new backend, just a "carLoanLeads" collection.
───────────────────────────────────────────────────────────── */
const ENQUIRY_LOAN_TYPES = [
  { value: "Car", image: loanTypeCarImg },
  { value: "Home", image: loanTypeHomeImg },
  { value: "Commercial", image: loanTypeCommercialImg },
  { value: "Personal", image: loanTypePersonalImg },
  { value: "Gold", image: loanTypeGoldImg },
  { value: "Business", image: loanTypeBusinessImg },
  { value: "Travel", image: loanTypeTravelImg },
];

const MIN_LOAN_AMOUNT = 10000;
const MAX_LOAN_AMOUNT = 1000000000; // ₹100 Cr — sanity cap only

// "500000" → "5 Lakh", "12500000" → "1.25 Crore" (Indian numbering).
function amountInWords(raw) {
  const n = Number(raw);
  if (!n) return "";
  const fmt = (v) => Number(v.toFixed(2)).toString();
  if (n >= 1e7) return `${fmt(n / 1e7)} Crore`;
  if (n >= 1e5) return `${fmt(n / 1e5)} Lakh`;
  if (n >= 1e3) return `${fmt(n / 1e3)} Thousand`;
  return "";
}

const INDIA_STATES = Array.from(new Set(indiaCitiesData.data.map((entry) => entry.state))).sort();

const CITIES_BY_STATE = indiaCitiesData.data.reduce((acc, entry) => {
  if (!acc[entry.state]) acc[entry.state] = [];
  acc[entry.state].push(entry.city);
  return acc;
}, {});
Object.keys(CITIES_BY_STATE).forEach((state) => CITIES_BY_STATE[state].sort());

// This apply page's own benefits list (longer than the home page's
// `carLoanBenefits` from data.js, which stays untouched so the home
// section keeps its original 4-item content).
// Hero proof points — compact icon pills under the headline.
const HERO_PROOF_POINTS = [
  { label: "All types of loans", icon: Layers },
  { label: "Lowest rates", icon: Percent },
  { label: "40+ banks & lenders", icon: Landmark },
];

// Hero spotlight + chips. `formValue` matches ENQUIRY_LOAN_TYPES so the
// hero CTA can pre-select the loan type in the enquiry form. `width` sizes
// each cut-out so they read at a similar visual weight inside the orbit;
// `tile` is the small image used on the orbit bubble.
const HERO_LOAN_TYPES = [
  { label: "Car Loan", formValue: "Car", image: heroLoanCarImg, tile: loanTypeCarImg, width: "w-[60%]", alt: "New car wrapped with a red ribbon" },
  { label: "Home Loan", formValue: "Home", image: heroLoanHomeImg, tile: loanTypeHomeImg, width: "w-[50%]", alt: "Two-storey family home" },
  { label: "Commercial Vehicle Loan", formValue: "Commercial", image: heroLoanCommercialImg, tile: loanTypeCommercialImg, width: "w-[46%]", alt: "Truck and cars" },
  { label: "Personal Loan", formValue: "Personal", image: heroLoanPersonalImg, tile: loanTypePersonalImg, width: "w-[34%]", alt: "Stack of cash and coins" },
  { label: "Gold Loan", formValue: "Gold", image: heroLoanGoldImg, tile: loanTypeGoldImg, width: "w-[27%]", alt: "Gold ring" },
  { label: "Business Loan", formValue: "Business", image: heroLoanBusinessImg, tile: loanTypeBusinessImg, width: "w-[33%]", alt: "Business growth chart" },
  { label: "Travel Loan", formValue: "Travel", image: heroLoanTravelImg, tile: loanTypeTravelImg, width: "w-[56%]", alt: "Aeroplane taking off" },
];

const HERO_ROTATE_MS = 3000;

const LOAN_PROCESS_STEPS = [
  {
    number: "01",
    icon: ClipboardList,
    visual: "tell",
    panel: "bg-gradient-to-br from-[#f3f0ff] to-[#e9e4ff]",
    title: "Tell Us",
    description: "Select your loan type and share basic details.",
  },
  {
    number: "02",
    icon: Scale,
    visual: "compare",
    panel: "bg-gradient-to-br from-[#eaf6ff] to-[#e2eeff]",
    title: "Compare",
    description: "Explore options from 40+ banks & lenders, including available rates and terms.",
  },
  {
    number: "03",
    icon: BadgeCheck,
    visual: "benefit",
    title: "Choose & Get Your Benefit",
    description: "Choose your preferred bank and complete the loan process.",
    benefit: "1st Month EMI FREE*",
  },
  {
    number: "04",
    icon: Coins,
    visual: "rewards",
    panel: "bg-gradient-to-br from-[#f7efff] to-[#eee4ff]",
    title: "Benefits After First Month",
    description:
      "From the second month onwards, pay your eligible EMI and earn P2B Coins. Use your coins to claim eligible cashback and rewards for everyday needs.",
  },
];

const CREDIT_LIFECYCLE_TOPUP = {
  title: "Top-Up Personal Loan",
  badge: "Quick liquidity",
  description:
    "Before taking a new loan, check what you can unlock from your existing loan. Get more liquidity at a potentially lower interest rate—without starting your borrowing journey from scratch.",
  tags: ["Top-up", "Liquidity"],
};

const CREDIT_LIFECYCLE_RATESWITCH = {
  title: "RateSwitch",
  badge: "Smart refinancing",
  description: "Discover potential refinancing and balance-transfer opportunities.",
  tags: ["Refinancing", "Balance transfer"],
};

const CREDIT_LIFECYCLE_TOOLS = [
  {
    title: "AI Liability Advisor",
    icon: Sparkles,
    description: "Get guidance on your loans, EMIs and next financial action.",
    tags: ["AI powered"],
  },
  {
    title: "Home Finance OS",
    icon: Home,
    description: "Manage financing, balance transfer, top-up and protection.",
    tags: ["Top-up", "Protection"],
  },
  {
    title: "Vehicle Financial OS",
    icon: Car,
    description: "Manage vehicle financing, insurance, servicing, fuel and upgrades.",
    tags: ["Insurance", "Servicing", "Fuel"],
  },
];

const CREDIT_LIFECYCLE_DEBT_OPTIMIZER = {
  title: "Debt Optimizer",
  description: "Explore EMI, tenure, refinancing and prepayment opportunities.",
  tags: ["EMI", "Tenure", "Prepayment"],
};

const CREDIT_LIFECYCLE_HEALTH = {
  title: "Liability Health",
  description: "Understand your repayment and overall debt position.",
};

const CUSTOMER_TESTIMONIALS = [
  {
    id: "t1",
    image: customerLoanImage1,
    name: "Ananya Mehta",
    city: "Mumbai",
    tag: "Kia Carens • Loan of ₹9 Lakh",
    quote:
      "P2B made the entire car loan process really simple. The interest rate was great, and the team guided me throughout. Very smooth experience.",
  },
  {
    id: "t2",
    image: customerLoanImage2,
    name: "Rahul Kapoor",
    city: "Bengaluru",
    tag: "Tata Harrier • Loan of ₹11 Lakh",
    quote:
      "Got my car loan approved quickly through P2B. The process was transparent, documentation was easy, and everything was explained clearly.",
  },
  {
    id: "t3",
    image: customerLoanImage3,
    name: "Neha Verma",
    city: "Delhi",
    tag: "Verna • Loan of ₹6 Lakh",
    quote:
      "The loan process with P2B was quick and hassle-free. From application to approval, everything was handled smoothly. Really happy with the experience.",
  },
  {
    id: "t4",
    image: customerLoanImage4,
    name: "Karan Malhotra",
    city: "Gurgaon",
    tag: "Hyundai Creta • Loan of ₹10 Lakh",
    quote:
      "P2B made my car loan journey stress-free. The team was helpful, the process was easy to understand, and the approval was quick.",
  },
];

/* Homepage FAQ. Keep in sync with HOME_FAQ in src/seo/pageSeo.js
   (Google FAQ rich-result schema must match the visible answers). */
const LOAN_FAQS = [
  {
    question: "How does the first month EMI cashback work?",
    blocks: [
      { t: "p", c: "You pay your first EMI to the lender as usual. Before your final loan approval, P2B will clearly show you the applicable cashback percentage and cashback amount, so you know your benefit before proceeding." },
      { t: "p", c: "For example:" },
      { t: "ul", c: ["First EMI: ₹20,000", "P2B Cashback: 80%", "Cashback: ₹16,000"] },
      { t: "p", c: "Or:" },
      { t: "ul", c: ["First EMI: ₹20,000", "P2B Cashback: 100%", "Cashback: ₹20,000"] },
      { t: "p", c: "Once your eligible loan is successfully disbursed through the bank, P2B provides the applicable cashback to your bank account." },
    ],
  },
  {
    question: "Secret trick behind the first month EMI cashback?",
    blocks: [
      { t: "ul", c: [
        "We don’t hide the 1st EMI cashback in your processing charges — zero processing fee bumps.",
        "Get the exact same interest rates offline with banks — no markups.",
        "No insurance traps.",
        "No tenure extension.",
        "No double EMI next month: your second month EMI is just a normal, single EMI.",
        "No CIBIL impact.",
      ] },
    ],
  },
  {
    question: "Which loans are eligible for the First Month EMI FREE* benefit?",
    answer:
      "All types of loans are eligible for the First Month EMI FREE* benefit — Car loan, Home loan, Commercial Vehicle loan, Personal loan, Gold loan, Business loan and Travel loan.",
  },
  {
    question: "How does P2B help me find a loan?",
    answer:
      "Tell us what loan you need and share your basic details. P2B helps you explore available options from 40+ banks and lenders, including applicable interest rates, loan amounts and terms.",
  },
  {
    question: "Does P2B guarantee the lowest interest rate?",
    answer:
      "Interest rates depend on your profile, loan type, credit history, income, lender policies and other factors. P2B helps you discover and compare available options; the final rate is determined by the selected lender.",
  },
  {
    question: "What happens after my first month?",
    answer:
      "From the second month onwards, pay your eligible EMI as usual. You can earn P2B Coins through eligible activities and use them to claim applicable cashback and rewards.",
  },
  {
    question: "Will I get the same interest rate through P2B as I would by applying directly to the bank?",
    answer:
      "Yes. P2B helps you explore loan options from banks and lenders at their applicable interest rates. You get the same lender-determined rate you would be offered through the bank, while also getting the First Month EMI FREE* benefit and the convenience of completing the process from the comfort of your home.",
  },
  {
    question: "Is P2B a bank or lender?",
    answer:
      "P2B is a technology platform that helps customers discover and explore available loan options from banks and lenders. The selected lender is responsible for approving and disbursing the loan.",
  },
];

const formatDate = (date) =>
  date
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", "");

const validationSchema = yup.object({
  loanType: yup.string().required("Please select a loan type"),
  loanAmount: yup
    .string()
    .required("Please enter the loan amount")
    .test("min", `Minimum amount is ₹${MIN_LOAN_AMOUNT.toLocaleString("en-IN")}`, (v) => !v || Number(v) >= MIN_LOAN_AMOUNT)
    .test("max", "Please enter a valid amount", (v) => !v || Number(v) <= MAX_LOAN_AMOUNT),
  name: yup.string().trim().required("Name is required").min(3, "Name must be at least 3 characters"),
  phoneNumber: yup
    .string()
    .required("Mobile number is required")
    .matches(/^[6-9][0-9]{9}$/, "Enter a valid 10-digit mobile number"),
  state: yup.string().required("Please select a state"),
  city: yup.string().required("Please select a city"),
});

const SUBMIT_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
const SUBMIT_COOLDOWN_KEY = "carLoanLastSubmission";
// Small, non-sensitive summary shown on the thank-you state (first name,
// loan type/amount, city, last 4 digits of the mobile only).
const LAST_ENQUIRY_KEY = "p2bLastEnquiry";


// Fixed-size dropdown popup (dark card, scrollable options) used for every
// "pick one" field in the form — replaces the native <select>, which renders
// inconsistently across browsers/OS, with a consistent custom UI.
function CustomSelect({ value, onChange, onBlur, options, placeholder, disabled, touched, error, triggerClassName, dropDirection = "down" }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        onBlur={onBlur}
        className={`${triggerClassName} flex items-center justify-between gap-2 text-left ${
          disabled ? "cursor-not-allowed opacity-50" : ""
        }`}
      >
        <span className={`truncate ${value ? "text-[#0d0828]" : "text-[#0d0828]/35"}`}>{value || placeholder}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#0d0828]/40 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && !disabled && (
        <div
          className={`absolute left-0 right-0 z-30 max-h-64 overflow-y-auto rounded-2xl border border-[#eceafb] bg-white p-2 shadow-[0_20px_45px_-18px_rgba(13,8,40,0.35)] ${
            dropDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {options.length === 0 ? (
            <p className="px-3 py-2.5 text-sm text-[#0d0828]/40">No options available</p>
          ) : (
            options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={`block w-full rounded-xl px-3 py-2.5 text-left text-[14px] transition ${
                  option === value ? "bg-[#4328E7]/10 text-[#4328E7] font-semibold" : "text-[#0d0828]/80 hover:bg-[#faf9ff]"
                }`}
              >
                {option}
              </button>
            ))
          )}
        </div>
      )}

      {touched && error && <p className="mt-1 text-sm text-red-300">{error}</p>}
    </div>
  );
}

// Photo + name/city/tag/stars/quote overlay for one "Happy customers" card.
// Shared by the mobile one-at-a-time scroller and the desktop marquee below —
// only the surrounding wrapper's size/behaviour differs between the two.
/* Tilted-ring orbit of every loan type around the hero spotlight.
   Positions are driven by requestAnimationFrame (not React state) so the
   30s orbit costs no re-renders. Back of the ring = smaller, fainter and
   behind the spotlight image (z 5); front = larger and in front (z 30).
   Pauses on hover (so the moving bubbles are easy to click), when the
   hero is off-screen, and never moves for reduced-motion users. */
const ORBIT_PERIOD_MS = 30000;
const ORBIT_RX = 46; // % of stage width
const ORBIT_RX_PHONE = 38; // narrower on phones so the labels stay on screen
const ORBIT_RY = 40; // % of stage height

function orbitPlacement(phi, rx = ORBIT_RX) {
  const c = Math.cos(phi);
  const d = (1 - c) / 2; // 0 = back of ring, 1 = front
  return {
    left: `${50 + rx * Math.sin(phi)}%`,
    top: `${50 - ORBIT_RY * c}%`,
    transform: `translate(-50%, -50%) scale(${(0.68 + 0.42 * d).toFixed(3)})`,
    opacity: (0.45 + 0.55 * d).toFixed(3),
    zIndex: d > 0.3 ? 30 : 5, // only the top-back arc passes behind the image
  };
}

function HeroOrbit({ loans, activeIndex, onSelect, paused, reducedMotion }) {
  const wrapRef = useRef(null);
  const bubbleRefs = useRef([]);
  const angleRef = useRef(0);
  const pausedRef = useRef(paused);
  const visibleRef = useRef(true);
  const step = (2 * Math.PI) / loans.length;
  const isPhone = useMediaQuery("(max-width: 639px)");
  const rx = isPhone ? ORBIT_RX_PHONE : ORBIT_RX;

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = now - last;
      last = now;
      if (!pausedRef.current && visibleRef.current) {
        angleRef.current += (dt * 2 * Math.PI) / ORBIT_PERIOD_MS;
        bubbleRefs.current.forEach((b, i) => {
          if (!b) return;
          const p = orbitPlacement(angleRef.current + i * step, rx);
          b.style.left = p.left;
          b.style.top = p.top;
          b.style.transform = p.transform;
          b.style.opacity = p.opacity;
          b.style.zIndex = String(p.zIndex);
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion, step, rx]);

  return (
    <div ref={wrapRef} className="absolute inset-0">
      {/* The ring itself */}
      <div
        aria-hidden="true"
        className="absolute rounded-[50%] border border-dashed border-[#4328E7]/25"
        style={{ left: `${50 - rx}%`, right: `${50 - rx}%`, top: `${50 - ORBIT_RY}%`, bottom: `${50 - ORBIT_RY}%` }}
      />
      {loans.map((loan, i) => {
        const active = i === activeIndex;
        return (
          <button
            key={loan.label}
            ref={(el) => (bubbleRefs.current[i] = el)}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`Show ${loan.label}`}
            title={loan.label}
            // Constant initial placement; the rAF loop takes over on the client.
            style={orbitPlacement(i * step, rx)}
            className="group absolute flex flex-col items-center gap-1"
          >
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_12px_24px_-10px_rgba(13,8,40,0.45)] transition duration-300 sm:h-14 sm:w-14 lg:h-[60px] lg:w-[60px] ${
                active
                  ? "ring-[3px] ring-[#4328E7] shadow-[0_14px_30px_-8px_rgba(67,40,231,0.75)]"
                  : "ring-1 ring-[#eceafb] group-hover:ring-2 group-hover:ring-[#4328E7]/50"
              }`}
            >
              <img src={loan.tile} alt="" aria-hidden="true" className="h-[70%] w-[70%] object-contain" />
            </span>
            <span
              aria-hidden="true"
              className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold leading-tight shadow-[0_6px_14px_-8px_rgba(13,8,40,0.35)] transition sm:text-[11px] lg:text-xs ${
                active ? "bg-[#4328E7] text-white" : "bg-white/90 text-[#0d0828]/80 ring-1 ring-[#eceafb]"
              }`}
            >
              {loan.formValue} Loan
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* Mini illustrations for the "4 Simple Steps" cards — built in markup
   (no images) so they stay crisp and on-brand. Deliberately generic:
   no real bank names or made-up rates, just the shape of each step. */
function StepVisual({ kind }) {
  if (kind === "tell") {
    return (
      <div className="w-[240px] max-w-full rounded-2xl bg-white p-4 shadow-[0_18px_40px_-22px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb]">
        <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Loan type</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[
            { label: "Car", icon: Car, active: true },
            { label: "Home", icon: Home },
            { label: "Personal", icon: IndianRupee },
            { label: "Gold", icon: Gem },
          ].map(({ label, icon: Icon, active }) => (
            <span
              key={label}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                active ? "bg-[#4328E7] text-white" : "bg-[#f3f1ff] text-[#0d0828]/70"
              }`}
            >
              <Icon className="h-3 w-3" strokeWidth={2.5} />
              {label}
            </span>
          ))}
        </div>
        <div className="mt-3.5 space-y-2">
          {[
            { label: "Name", w: "w-20" },
            { label: "Mobile", w: "w-24" },
          ].map((f) => (
            <div key={f.label} className="flex items-center justify-between rounded-lg border border-[#eceafb] px-2.5 py-2">
              <span className="text-[10px] font-bold text-[#0d0828]/40">{f.label}</span>
              <span className={`h-1.5 ${f.w} rounded-full bg-[#0d0828]/10`} />
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-center gap-1.5 rounded-lg bg-[#4328E7] py-2 text-[11px] font-black text-white">
          Continue <ArrowRight className="h-3 w-3" strokeWidth={3} />
        </div>
      </div>
    );
  }

  if (kind === "compare") {
    return (
      <div className="w-[240px] max-w-full rounded-2xl bg-white p-4 shadow-[0_18px_40px_-22px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb]">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Your options</p>
          <span className="rounded-full bg-[#E6FAF3] px-2 py-0.5 text-[10px] font-black text-[#0f9f73]">40+ lenders</span>
        </div>
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`relative flex items-center gap-2.5 rounded-xl px-2.5 py-2 ${
                i === 0 ? "bg-[#f3f1ff] ring-1 ring-[#4328E7]/30" : "bg-[#fafafe]"
              }`}
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  i === 0 ? "bg-[#4328E7] text-white" : "bg-white text-[#0d0828]/40 ring-1 ring-[#eceafb]"
                }`}
              >
                <Landmark className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
              <div className="flex-1 space-y-1">
                <span className={`block h-1.5 rounded-full ${i === 0 ? "w-16 bg-[#4328E7]/50" : "w-14 bg-[#0d0828]/[0.12]"}`} />
                <span className="block h-1.5 w-10 rounded-full bg-[#0d0828]/[0.08]" />
              </div>
              <div className="text-right">
                <p className="text-[9px] font-bold uppercase text-[#0d0828]/35">Rate</p>
                <span className={`mt-0.5 block h-1.5 w-8 rounded-full ${i === 0 ? "bg-[#12B886]" : "bg-[#0d0828]/[0.12]"}`} />
              </div>
              {i === 0 && (
                <span className="absolute -top-2 right-2 rounded-full bg-[#12B886] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-white">
                  Best match
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === "rewards") {
    return (
      <div className="w-[240px] max-w-full rounded-2xl bg-white p-4 text-[#0d0828] shadow-[0_18px_40px_-22px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb]">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">P2B Coins</p>
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#4328E7] to-[#7b64ff] text-white shadow-[0_6px_14px_-6px_rgba(67,40,231,0.9)]">
            <Coins className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
        </div>
        <div className="mt-2.5 flex items-center justify-between rounded-xl bg-[#f3f1ff] px-3 py-2">
          <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#0d0828]/70">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#12B886]" strokeWidth={2.75} />
            Month 2 EMI paid
          </span>
          <span className="text-[10px] font-black text-[#4328E7]">+ Coins</span>
        </div>
        <p className="mt-3 text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Claim rewards</p>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {[
            { label: "Cashback", icon: IndianRupee },
            { label: "Shopping", icon: ShoppingBag },
            { label: "Bills", icon: Receipt },
          ].map(({ label, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center gap-1 rounded-xl bg-[#fafafe] px-1 py-2 ring-1 ring-[#eceafb]">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EDE9FE] text-[#4328E7]">
                <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
              <span className="text-[9.5px] font-bold text-[#0d0828]/70">{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // "benefit"
  return (
    <div className="w-[240px] max-w-full rounded-2xl bg-white p-4 text-[#0d0828] shadow-[0_18px_40px_-18px_rgba(13,8,40,0.55)]">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E6FAF3] text-[#0f9f73]">
          <CheckCircle2 className="h-4 w-4" strokeWidth={2.75} />
        </span>
        <p className="text-[12px] font-black">Bank selected</p>
      </div>
      <p className="mt-3.5 text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Your EMIs</p>
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-[#4328E7] to-[#6d55ff] px-3 py-2 text-white shadow-[0_10px_22px_-10px_rgba(67,40,231,0.9)]">
          <span className="whitespace-nowrap text-[11px] font-bold">Month 1</span>
          <span className="ml-3 flex items-center gap-1.5">
            <span className="rounded-full bg-white px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-[#4328E7]">Free*</span>
          </span>
        </div>
        {["Month 2", "Month 3"].map((m) => (
          <div key={m} className="flex items-center justify-between rounded-xl bg-[#fafafe] px-3 py-2">
            <span className="text-[11px] font-bold text-[#0d0828]/45">{m}</span>
            <span className="h-1.5 w-12 rounded-full bg-[#0d0828]/[0.12]" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialCard({ testimonial }) {
  return (
    <>
      <img
        src={testimonial.image}
        alt={testimonial.name}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: "center 25%" }}
      />
      {/* Bottom black gradient so the name/tag/quote stay readable over
          whatever the photo looks like there. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black via-black/70 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 p-5">
        <p className="text-[15px] font-bold text-white">{testimonial.name}</p>
        <p className="text-[12px] text-white/70">{testimonial.city}</p>

        <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.05em] text-white backdrop-blur-sm">
          <Car className="h-3.5 w-3.5" strokeWidth={2} />
          {testimonial.tag}
        </span>

        <div className="mt-3 flex gap-0.5 text-[#8f83ff]">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <Star key={starIndex} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
          ))}
        </div>

        <p className="mt-2 text-[13px] italic leading-5 text-white/85">
          <Quote className="mr-1 inline-block h-3.5 w-3.5 -translate-y-0.5 text-white/40" />
          {testimonial.quote}
        </p>
      </div>
    </>
  );
}

export default function CarLoanApply() {
  // Hero loan spotlight
  const prefersReducedMotion = usePrefersReducedMotion();
  const [heroLoanIndex, setHeroLoanIndex] = useState(0);
  // Hover only freezes the orbit ring (so bubbles are easy to click); the
  // loan itself keeps changing every 3s. Picking a loan jumps to it and the
  // 3s timer restarts from there (the effect re-runs on every index change).
  const [heroPaused, setHeroPaused] = useState(false);
  const heroRotating = !prefersReducedMotion;
  const heroLoan = HERO_LOAN_TYPES[heroLoanIndex];

  useEffect(() => {
    if (!heroRotating) return undefined;
    const t = setTimeout(
      () => setHeroLoanIndex((i) => (i + 1) % HERO_LOAN_TYPES.length),
      HERO_ROTATE_MS
    );
    return () => clearTimeout(t);
  }, [heroLoanIndex, heroRotating]);

  // Always land at the top of this page, regardless of the scroll
  // position on whatever page the user navigated here from.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dsaBanks, setDsaBanks] = useState([]);
  const dsaBanksScrollRef = useRef(null);
  const dsaBanksDragRef = useRef({ isDown: false, startX: 0, startScrollLeft: 0 });
  const dsaBanksAutoPausedRef = useRef(false);
  const dsaBanksResumeTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchDsaBanks = async () => {
      const q = query(collection(db, "dsaBanks"), where("active", "==", true), orderBy("rank"));
      const snapshot = await getDocs(q);
      const banks = snapshot.docs.map((doc) => doc.data());
      setDsaBanks(banks);
    };
    fetchDsaBanks();
  }, []);

  // Plain `overflow-x-auto` only scrolls via a trackpad/scrollbar/touch —
  // a mouse click-and-drag does nothing by default, so wire that up here.
  // Guarded to pointerType === "mouse" only: touch already scrolls this
  // element natively, and calling preventDefault() on a touch-driven
  // pointermove (or even just doing the scrollLeft math on it) is what was
  // fighting the browser's own touch-scroll physics and making the drag
  // feel janky on mobile.
  const handleDsaBanksPointerDown = (e) => {
    const el = dsaBanksScrollRef.current;
    if (!el) return;
    clearTimeout(dsaBanksResumeTimeoutRef.current);
    dsaBanksAutoPausedRef.current = true;
    if (e.pointerType !== "mouse") return;
    dsaBanksDragRef.current = { isDown: true, startX: e.pageX, startScrollLeft: el.scrollLeft };
  };
  const handleDsaBanksPointerMove = (e) => {
    const drag = dsaBanksDragRef.current;
    const el = dsaBanksScrollRef.current;
    if (!drag.isDown || !el || e.pointerType !== "mouse") return;
    e.preventDefault();
    el.scrollLeft = drag.startScrollLeft - (e.pageX - drag.startX);
  };
  const handleDsaBanksPointerUp = (e) => {
    if (e.pointerType === "mouse") dsaBanksDragRef.current.isDown = false;
    // Touch keeps scrolling under its own momentum after release — give it
    // a moment to settle before auto-scroll takes the wheel again.
    clearTimeout(dsaBanksResumeTimeoutRef.current);
    dsaBanksResumeTimeoutRef.current = setTimeout(() => {
      dsaBanksAutoPausedRef.current = false;
    }, e.pointerType === "mouse" ? 0 : 1200);
  };

  // Auto-scroll the bank-logo row. Runs via scrollLeft (not a CSS
  // transform) so it shares the exact same axis as the manual drag above —
  // dragging simply overrides scrollLeft for a moment, and auto-scroll
  // picks back up from wherever the user left it. The list below is
  // rendered twice back-to-back, so once scrollLeft passes the width of
  // one copy, it's snapped back by that same amount — both halves look
  // identical, so the reset is invisible.
  //
  // Uses a running float + el.scrollTo(...) rather than reading back and
  // incrementing el.scrollLeft directly: iOS Safari rounds scrollLeft to
  // an integer and, on some overflow-x:auto elements, silently drops a
  // sub-pixel write instead of accumulating it — the position never
  // visibly moves even though this loop is running every frame. Tracking
  // the target position ourselves sidesteps that.
  useEffect(() => {
    if (dsaBanks.length === 0) return;
    const el = dsaBanksScrollRef.current;
    if (!el) return;
    let frameId;
    let position = el.scrollLeft;
    const speedPxPerFrame = 0.6;
    const step = () => {
      if (!dsaBanksAutoPausedRef.current) {
        const loopWidth = el.scrollWidth / 2;
        position += speedPxPerFrame;
        if (position >= loopWidth) {
          position -= loopWidth;
        }
        el.scrollTo({ left: position, behavior: "auto" });
      } else {
        // Stay in sync with wherever a drag/native scroll left it, so
        // auto-scroll resumes from there instead of jumping back.
        position = el.scrollLeft;
      }
      frameId = requestAnimationFrame(step);
    };
    frameId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(dsaBanksResumeTimeoutRef.current);
    };
  }, [dsaBanks]);

  // One submission per hour, enforced via localStorage so it survives a
  // refresh (there's no user auth/session here to key it off instead).
  const [cooldownMs, setCooldownMs] = useState(0);
  const [lastEnquiry, setLastEnquiry] = useState(null);

  // Single-open FAQ accordion (-1 = all collapsed).
  const [openFaqIndex, setOpenFaqIndex] = useState(-1);

  // Mobile "Happy customers" carousel — auto-advances on a timer, but a
  // manual swipe (tracked via IntersectionObserver below) becomes the new
  // starting point for the next auto-advance instead of fighting it.
  const testimonialScrollRef = useRef(null);
  const testimonialCardRefs = useRef([]);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const activeTestimonialIndexRef = useRef(0);

  useEffect(() => {
    activeTestimonialIndexRef.current = activeTestimonialIndex;
  }, [activeTestimonialIndex]);

  useEffect(() => {
    const container = testimonialScrollRef.current;
    const cards = testimonialCardRefs.current.filter(Boolean);
    if (!container || cards.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!mostVisible) return;
        const index = cards.indexOf(mostVisible.target);
        if (index !== -1) setActiveTestimonialIndex(index);
      },
      { root: container, threshold: 0.6 }
    );
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  // Only auto-advance while the carousel is actually visible on screen —
  // otherwise nothing should move at all (see the scrollBy below too:
  // it's scoped to the horizontal track itself, never the page).
  const isTestimonialVisibleRef = useRef(false);

  useEffect(() => {
    const container = testimonialScrollRef.current;
    if (!container) return undefined;
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isTestimonialVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.4 }
    );
    visibilityObserver.observe(container);
    return () => visibilityObserver.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTestimonialVisibleRef.current) return;
      const container = testimonialScrollRef.current;
      const cards = testimonialCardRefs.current.filter(Boolean);
      if (!container || cards.length === 0) return;
      const nextIndex = (activeTestimonialIndexRef.current + 1) % cards.length;
      const card = cards[nextIndex];
      const containerRect = container.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const delta = cardRect.left - containerRect.left - (containerRect.width - cardRect.width) / 2;
      container.scrollBy({ left: delta, behavior: "smooth" });
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const checkCooldown = () => {
      const last = localStorage.getItem(SUBMIT_COOLDOWN_KEY);
      if (!last) {
        setCooldownMs(0);
        return;
      }
      const remaining = SUBMIT_COOLDOWN_MS - (Date.now() - parseInt(last, 10));
      if (remaining > 0) {
        setCooldownMs(remaining);
        try {
          const saved = JSON.parse(localStorage.getItem(LAST_ENQUIRY_KEY) || "null");
          setLastEnquiry((prev) => prev || saved);
        } catch {
          /* ignore a malformed summary */
        }
      } else {
        localStorage.removeItem(SUBMIT_COOLDOWN_KEY);
        localStorage.removeItem(LAST_ENQUIRY_KEY);
        setCooldownMs(0);
        setLastEnquiry(null);
      }
    };
    checkCooldown();
    const interval = setInterval(checkCooldown, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formik = useFormik({
    initialValues: { loanType: "", loanAmount: "", name: "", phoneNumber: "", state: "", city: "" },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (cooldownMs > 0) return; // extra guard — the button is disabled too
      setLoading(true);
      try {
        const userAgent = navigator.userAgent;
        const platform = navigator.platform;
        const dateTime = formatDate(new Date());

        // Same "carLoanLeads" collection as before (existing Firestore
        // rules/dashboards), now carrying every loan type. "source"
        // separates these from the older car-only enquiries.
        await addDoc(collection(db, "carLoanLeads"), {
          source: "home-enquiry-all-loans",
          loanType: values.loanType,
          loanAmount: Number(values.loanAmount),
          name: values.name.trim(),
          phoneNumber: values.phoneNumber,
          state: values.state,
          city: values.city,
          timestamp: dateTime,
          device: userAgent,
          platform,
          dateTime,
          createdOn: new Date(),
        });

        const summary = {
          firstName: values.name.trim().split(/\s+/)[0],
          loanType: values.loanType,
          loanAmount: values.loanAmount,
          city: values.city,
          phoneLast4: values.phoneNumber.slice(-4),
        };
        localStorage.setItem(SUBMIT_COOLDOWN_KEY, Date.now().toString());
        try {
          localStorage.setItem(LAST_ENQUIRY_KEY, JSON.stringify(summary));
        } catch {
          /* storage full/blocked — the thank-you state just shows less detail */
        }
        setLastEnquiry(summary);
        setCooldownMs(SUBMIT_COOLDOWN_MS);
        resetForm();
        // Success is shown inline (the form card turns into a thank-you
        // panel); the modal is kept for errors only.
        document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (error) {
        console.error("Error adding document: ", error);
        setModalTitle("Something Went Wrong");
        setModalMessage("We couldn't submit your application. Please try again in a moment.");
        setIsModalOpen(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const formLabelClass = "mb-2 block text-[13px] font-bold text-[#0d0828]/70";

  const formInputClass = (touched, error) =>
    // 16px is the minimum iOS/Android leave alone when focusing a text
    // field — anything smaller (this was 15px) makes the browser zoom the
    // whole viewport in on focus, which is the "zooms in when I tap a
    // field" bug reported on mobile.
    `w-full rounded-2xl border bg-[#faf9ff] px-4 py-3.5 text-[16px] text-[#0d0828] placeholder-[#0d0828]/35 outline-none transition focus:border-[#4328E7] focus:ring-2 focus:ring-[#4328E7]/20 ${
      touched && error ? "border-red-400" : "border-[#eceafb]"
    }`;

  return (
    <div className="min-h-screen bg-white font-montserrat text-[#0d0828]">
      {/* Animated ring that travels around the lead-form card, matching
          the reference page's moving-border highlight. */}
      <style>{`
        @keyframes carLoanBorderSpin {
          to { transform: rotate(360deg); }
        }
        .car-loan-border-spin {
          animation: carLoanBorderSpin 3.2s linear infinite;
        }
        @keyframes carLoanLenderScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        /* The global "img { content-visibility: auto }" rule (index.css,
           added for scroll perf) skips rendering images the browser judges
           off-viewport — but inside this infinite marquee, the whole point
           is that most of the (duplicated) cards start off to the right and
           are carried into view purely by the track's own transform
           animation. Mobile Safari applies that skip based on each image's
           own layout box and never re-evaluates it as the transform moves
           it, so most images simply never render there — the strip looks
           empty/frozen on mobile even though it animates fine on desktop.
           Force these images to always render. */
        .car-loan-testimonial-track img {
          content-visibility: visible;
        }
        .car-loan-testimonial-track {
          animation: carLoanLenderScroll 38s linear infinite;
        }
        /* Bank-logo row — plain drag/swipe-to-scroll, not an animated
           marquee, but it hits the same two mobile issues as the tracks
           above: the global content-visibility:auto rule can skip
           rendering logos that start out of view, and axis locking is
           needed so a horizontal drag doesn't fight the page's own
           vertical scroll.
           Deliberately NOT setting -webkit-overflow-scrolling: touch
           here — that property puts the element on its own scroll layer
           on iOS Safari, and that layer often ignores JS-driven
           scrollLeft changes (the auto-scroll below) until the user has
           first scrolled it manually. Modern iOS already gives any
           overflow:auto element native momentum scrolling without it. */
        .car-loan-dsa-banks-scroll {
          touch-action: pan-x;
          scroll-behavior: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .car-loan-dsa-banks-scroll::-webkit-scrollbar {
          display: none;
        }
        .car-loan-dsa-banks-scroll img {
          content-visibility: visible;
        }
        @keyframes carLoanHeroFade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: none; }
        }
        .car-loan-hero-fade {
          display: inline-block;
          animation: carLoanHeroFade 0.45s ease-out;
        }
        .car-loan-thanks-in {
          animation: carLoanHeroFade 0.5s ease-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .car-loan-hero-fade, .car-loan-thanks-in { animation: none; }
        }
        .car-loan-testimonial-scroll,
        .car-loan-process-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .car-loan-testimonial-scroll::-webkit-scrollbar,
        .car-loan-process-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <ContactResponseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
      />

      {/* Light navbar — full dark wordmark now that the hero is light.
          Scrolls away with the page (not fixed/sticky). */}
      <header className="relative z-50 border-b border-[#eceafb]/70 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 sm:h-16 sm:px-6 lg:px-8">
          <Link to="/" aria-label="Go to P2B home" className="inline-flex items-center gap-2.5">
            <img src={p2bLogo} alt="" aria-hidden="true" className="h-8 w-8 rounded-[9px] sm:h-9 sm:w-9 sm:rounded-[10px]" />
            <span className="text-[22px] font-black tracking-[-0.02em] text-[#0d0828] sm:text-2xl">P2B</span>
          </Link>
          <a
            href="#apply"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#4328E7] px-4 py-2 text-[13px] font-bold text-white shadow-[0_10px_24px_-10px_rgba(67,40,231,0.9)] transition hover:bg-[#3a20d0] sm:px-5 sm:text-sm"
          >
            Apply Now
          </a>
        </div>
      </header>

      {/* ---------------------------------------------------------- */}
      {/* Hero: clean light, offer-led. Headline + proof points + CTA
          on the left, gift-wrapped car on the right, then a band that
          lists every loan type carrying the same First-EMI-free
          benefit. The lead form stays in its own section below
          (#apply). */}
      {/* ---------------------------------------------------------- */}
      <section className="relative overflow-hidden bg-white lg:flex lg:min-h-[calc(100svh-64px)] lg:flex-col lg:justify-center">
        {/* Desktop: the hero is sized to fit one screen below the
            header at 100% zoom — type and the spotlight scale with viewport
            height (vh clamps) so short laptop screens don't need a scroll. */}
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pt-10 pb-8 sm:px-6 sm:pt-14 lg:w-full lg:grid-cols-[1.05fr_1fr] lg:gap-6 lg:px-8 lg:pt-[clamp(24px,5vh,64px)] lg:pb-[clamp(32px,7vh,80px)]">
          {/* Left: offer */}
          <div className="text-center lg:text-left">
            <h1 className="text-[44px] font-black leading-[0.98] tracking-[-0.03em] text-[#0d0828] xs:text-5xl sm:text-7xl lg:text-[clamp(52px,9.2vh,84px)]">
              First month
              <br />
              <span className="bg-gradient-to-r from-[#4328E7] via-[#5b3bff] to-[#8a73ff] bg-clip-text text-transparent">
                EMI free
                <sup className="relative -translate-y-[0.35em] align-baseline text-[0.4em] font-black leading-none text-[#4328E7]">
                  *
                </sup>
              </span>
            </h1>
            <p className="mt-3 text-base font-semibold text-[#0d0828]/60 sm:text-lg lg:mt-4">
              Cashback on your first month’s EMI
            </p>

            {/* Proof points — icon pills */}
            <ul className="mt-7 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 lg:mt-[clamp(18px,3.2vh,30px)] lg:justify-start">
              {HERO_PROOF_POINTS.map(({ label, icon: Icon }) => (
                <li
                  key={label}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-white py-1.5 pl-1.5 pr-3.5 text-[13px] font-bold text-[#0d0828] shadow-[0_8px_20px_-12px_rgba(13,8,40,0.35)] ring-1 ring-[#eceafb] sm:pr-4 sm:text-[14px]"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EDE9FE] text-[#4328E7]">
                    <Icon className="h-[15px] w-[15px]" strokeWidth={2.25} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>

            {/* Get the app */}
            <div className="mt-8 flex flex-col items-center gap-3 lg:mt-[clamp(20px,4vh,40px)] lg:items-start">
              <p className="text-[12px] font-black uppercase tracking-[0.16em] text-[#0d0828]/45">Get the P2B app</p>
              <div className="flex items-center gap-3">
                <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="transition duration-300 hover:-translate-y-0.5">
                  <img src={appStoreBadge} alt="Download on the App Store" className="h-[42px] w-auto lg:h-[clamp(38px,6vh,46px)]" />
                </a>
                <a href={PLAYSTORE_URL} target="_blank" rel="noopener noreferrer" className="transition duration-300 hover:-translate-y-0.5">
                  <img src={playStoreBadge} alt="Get it on Google Play" className="h-[42px] w-auto lg:h-[clamp(38px,6vh,46px)]" />
                </a>
              </div>
            </div>
            <p className="mt-5 text-xs font-medium text-[#0d0828]/45 lg:mt-[clamp(12px,2.4vh,22px)]">*T&amp;C apply</p>
          </div>

          {/* Right: loan spotlight + orbit — rotates through every loan type
              (changes every 3s — a picked loan jumps in and the timer
              restarts; static for reduced-motion users). The orbit bubbles are its
              controls. */}
          <div
            className="relative mx-auto w-full max-w-[560px] lg:max-w-none"
            onMouseEnter={() => setHeroPaused(true)}
            onMouseLeave={() => setHeroPaused(false)}
          >
            <div className="relative z-10 aspect-[4/3] w-full lg:mx-auto lg:aspect-auto lg:h-[clamp(320px,58vh,520px)]">
              <HeroOrbit
                loans={HERO_LOAN_TYPES}
                activeIndex={heroLoanIndex}
                paused={heroPaused}
                reducedMotion={prefersReducedMotion}
                onSelect={setHeroLoanIndex}
              />
              {HERO_LOAN_TYPES.map((loan, i) => {
                const active = i === heroLoanIndex;
                return (
                  <img
                    key={loan.label}
                    src={loan.image}
                    alt={active ? loan.alt : ""}
                    aria-hidden={!active}
                    fetchpriority={i === 0 ? "high" : undefined}
                    className={`absolute left-1/2 top-1/2 z-10 max-h-[50%] -translate-x-1/2 object-contain drop-shadow-[0_24px_28px_rgba(13,8,40,0.22)] transition-all duration-700 ease-out motion-reduce:transition-none ${loan.width} ${
                      active ? "-translate-y-1/2 scale-100 opacity-100" : "pointer-events-none -translate-y-[44%] scale-95 opacity-0"
                    }`}
                  />
                );
              })}
            </div>

            {/* Floating offer badge — follows the loan in the spotlight */}
            <div className="absolute -top-2 right-2 z-40 min-w-[150px] rounded-2xl bg-white px-4 py-3 text-left shadow-[0_20px_45px_-18px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb] sm:right-6 lg:-top-4">
              <p key={heroLoan.label} className="car-loan-hero-fade text-[10px] font-bold uppercase tracking-[0.16em] text-[#0d0828]/50">
                {heroLoan.formValue} · 1st EMI
              </p>
              <p className="text-2xl font-black leading-none text-[#4328E7] sm:text-3xl">FREE*</p>
            </div>
          </div>
        </div>

      </section>

      {/* ---------------------------------------------------------- */}
      {/* dsaBanks — fetched from Firestore, active only, sorted by rank. */}
      {/* ---------------------------------------------------------- */}
      <section className="border-b border-[#eceafb] bg-white px-5 py-10 sm:px-6 lg:px-8">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.2em] text-[#0d0828]/40">
          Compare offers from 40+ banks and lenders, including
        </p>
        <div
          ref={dsaBanksScrollRef}
          className="car-loan-dsa-banks-scroll mt-6 flex cursor-grab items-center gap-8 overflow-x-auto active:cursor-grabbing"
          onPointerDown={handleDsaBanksPointerDown}
          onPointerMove={handleDsaBanksPointerMove}
          onPointerUp={handleDsaBanksPointerUp}
          onPointerLeave={handleDsaBanksPointerUp}
        >
          {[...dsaBanks, ...dsaBanks].map((bank, i) => (
            <img
              key={`${bank.id}-${i}`}
              src={bank.icon}
              alt={bank.title}
              draggable={false}
              className="h-10 w-auto shrink-0 select-none object-contain sm:h-12"
            />
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Lead-gen form — its own section right below the hero. No card/
          rectangle wrapper — the fields spread across the full section
          width in a responsive grid instead of a narrow floating box. */}
      {/* ---------------------------------------------------------- */}
      <section id="apply" className="scroll-mt-4 bg-white px-5 py-14 sm:px-6 lg:px-8 lg:py-14">
        <div className="mx-auto max-w-3xl lg:max-w-6xl">
          <div className="relative rounded-[28px] p-[1.5px]">
            {/* Rotating highlight ring — visible only as a thin border,
                thanks to the padding above and the solid card sitting
                on top of it. Clipped to its own layer (not the whole
                card) so dropdown popups inside the form can still
                overflow the card without being cut off. */}
            <div aria-hidden="true" className="absolute inset-0 overflow-hidden rounded-[28px]">
              <div
                className="car-loan-border-spin pointer-events-none absolute inset-[-40%] origin-center"
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 0%, rgba(67,40,231,0.85) 6%, transparent 16%, transparent 100%)",
                }}
              />
            </div>
            <div className="relative rounded-[26px] border border-[#eceafb] bg-white p-7 shadow-[0_30px_60px_-28px_rgba(13,8,40,0.3)] sm:p-9 lg:px-12 lg:py-10">
          {cooldownMs > 0 ? (
            /* Success state — replaces the whole form for the 1-hour
               resubmit cooldown (survives a reload via localStorage). */
            <div role="status" aria-live="polite" className="car-loan-thanks-in mx-auto max-w-4xl text-center">
              <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
                <span aria-hidden="true" className="absolute inset-0 animate-ping rounded-full bg-[#4328E7]/20 [animation-duration:1.8s] [animation-iteration-count:2] motion-reduce:hidden" />
                <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#4328E7] to-[#7b64ff] text-white shadow-[0_18px_36px_-12px_rgba(67,40,231,0.8)] ring-8 ring-[#4328E7]/10">
                  <Check className="h-10 w-10" strokeWidth={3} />
                </span>
              </div>

              <h2 className="mt-6 text-2xl font-black leading-tight tracking-[-0.02em] text-[#0d0828] sm:text-4xl">
                Thank you{lastEnquiry?.firstName ? `, ${lastEnquiry.firstName}` : ""}!
              </h2>
              <p className="mx-auto mt-2.5 max-w-lg text-[15px] font-medium leading-6 text-[#0d0828]/60 sm:text-base">
                We&apos;ve received your enquiry. A P2B loan expert will call you shortly to help you explore options.
              </p>

              {lastEnquiry && (
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                  {[
                    lastEnquiry.loanType && `${lastEnquiry.loanType} Loan`,
                    lastEnquiry.loanAmount && `₹${Number(lastEnquiry.loanAmount).toLocaleString("en-IN")}`,
                    lastEnquiry.city,
                  ]
                    .filter(Boolean)
                    .map((item) => (
                      <span key={item} className="rounded-full bg-[#f3f0ff] px-3.5 py-1.5 text-[13px] font-bold text-[#4328E7] ring-1 ring-[#4328E7]/15">
                        {item}
                      </span>
                    ))}
                </div>
              )}

              {/* What happens next */}
              <div className="mt-8 rounded-2xl border border-[#eceafb] bg-[#faf9ff] p-5 text-left sm:p-6">
                <p className="text-center text-[12px] font-black uppercase tracking-[0.16em] text-[#0d0828]/45 lg:text-left">
                  What happens next
                </p>
                <ol className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                  {[
                    {
                      icon: PhoneCall,
                      title: "Expert call",
                      body: lastEnquiry?.phoneLast4
                        ? `We'll call you on the number ending ${lastEnquiry.phoneLast4}.`
                        : "Our loan expert will call you shortly.",
                    },
                    { icon: Scale, title: "Compare offers", body: "Explore rates and terms from 40+ banks & lenders." },
                    { icon: Gift, title: "First Month EMI FREE*", body: "Choose your bank and get the benefit once your loan is disbursed." },
                  ].map(({ icon: Icon, title, body }, i) => (
                    <li key={title} className="flex items-start gap-3">
                      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#4328E7] shadow-sm ring-1 ring-[#eceafb]">
                        <Icon className="h-5 w-5" strokeWidth={2.25} />
                        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#4328E7] text-[9px] font-black text-white">
                          {i + 1}
                        </span>
                      </span>
                      <div>
                        <p className="text-[15px] font-black text-[#0d0828]">{title}</p>
                        <p className="mt-0.5 text-[13px] leading-5 text-[#0d0828]/60">{body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-5 flex flex-col items-center gap-2">
                {/* Small "refill" — clears the cooldown and brings the form back */}
                <button
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.removeItem(SUBMIT_COOLDOWN_KEY);
                      localStorage.removeItem(LAST_ENQUIRY_KEY);
                    } catch {
                      /* ignore */
                    }
                    setCooldownMs(0);
                    setLastEnquiry(null);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#4328E7]/20 bg-white px-3.5 py-1.5 text-[12px] font-bold text-[#4328E7] transition hover:border-[#4328E7]/45 hover:bg-[#f3f0ff]"
                >
                  <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
                  Fill another enquiry
                </button>
                <p className="text-[11px] font-medium text-[#0d0828]/40">*T&amp;C apply</p>
              </div>
            </div>
          ) : (
          <>
          <div className="text-center">
            <span className="inline-flex items-center rounded-full border border-[#4328E7]/15 bg-white px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#4328E7] shadow-sm">
              Enquiry
            </span>
            <h2 className="mt-4 text-2xl font-black leading-tight tracking-[-0.02em] text-[#0d0828] sm:text-4xl">
              Find Your Loan With{" "}
              <span className="bg-gradient-to-r from-[#4328E7] to-[#7c6bff] bg-clip-text text-transparent">P2B</span>
            </h2>
            <p className="mx-auto mt-2.5 max-w-md text-[15px] font-medium leading-6 text-[#0d0828]/60">
              Tell us what you need and we&apos;ll help you explore available options.
            </p>
          </div>

          <form onSubmit={formik.handleSubmit} className="mt-9 lg:mt-8" noValidate>
            {/* Loan type */}
            <div>
              <p className={formLabelClass}>Loan Type</p>
              <div role="radiogroup" aria-label="Loan type" className="flex flex-wrap justify-center gap-2 sm:flex-nowrap sm:gap-2.5">
                {ENQUIRY_LOAN_TYPES.map(({ value, image }) => {
                  const selected = formik.values.loanType === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        formik.setFieldValue("loanType", value);
                        formik.setFieldTouched("loanType", true, false);
                      }}
                      className={`group relative flex w-[calc(25%-6px)] flex-col items-center gap-1.5 rounded-2xl border px-1.5 pb-2.5 pt-3 text-[12px] font-bold transition duration-200 sm:w-auto sm:flex-1 sm:text-[12.5px] ${
                        selected
                          ? "border-[#4328E7] bg-[#f3f0ff] text-[#4328E7] shadow-[0_14px_28px_-16px_rgba(67,40,231,0.9)] ring-2 ring-[#4328E7]/20"
                          : "border-[#eceafb] bg-[#faf9ff] text-[#0d0828]/75 hover:-translate-y-0.5 hover:border-[#4328E7]/35 hover:bg-white"
                      }`}
                    >
                      {selected && (
                        <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#4328E7] text-white shadow-[0_4px_10px_-3px_rgba(67,40,231,0.9)] ring-2 ring-white">
                          <Check className="h-3 w-3" strokeWidth={3.5} />
                        </span>
                      )}
                      <span className="flex h-11 w-full items-center justify-center sm:h-12">
                        <img
                          src={image}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          className={`max-h-full max-w-[88%] object-contain transition duration-200 ${
                            selected ? "scale-110" : "group-hover:scale-105"
                          }`}
                        />
                      </span>
                      {value}
                    </button>
                  );
                })}
              </div>
              {formik.touched.loanType && formik.errors.loanType && (
                <p className="mt-1.5 text-sm text-red-500">{formik.errors.loanType}</p>
              )}
            </div>

            {/* Row 2 — amount, name, mobile (3 across on desktop) */}
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {/* Loan amount */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label htmlFor="loanAmount" className={formLabelClass}>Loan Amount</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[17px] font-black text-[#4328E7]">₹</span>
                <input
                  type="text"
                  inputMode="numeric"
                  id="loanAmount"
                  name="loanAmount"
                  placeholder="Enter amount"
                  autoComplete="off"
                  value={formik.values.loanAmount ? Number(formik.values.loanAmount).toLocaleString("en-IN") : ""}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").replace(/^0+/, "").slice(0, 10);
                    formik.setFieldValue("loanAmount", digits);
                  }}
                  onBlur={formik.handleBlur}
                  className={`${formInputClass(formik.touched.loanAmount, formik.errors.loanAmount)} pl-9 font-semibold`}
                />
                {formik.values.loanAmount && (
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-[#EDE9FE] px-2.5 py-1 text-[11px] font-black text-[#4328E7]">
                    {amountInWords(formik.values.loanAmount)}
                  </span>
                )}
              </div>
              {formik.touched.loanAmount && formik.errors.loanAmount && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.loanAmount}</p>
              )}
            </div>

            {/* Name + mobile */}

              <div>
                <label htmlFor="name" className={formLabelClass}>Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  autoComplete="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={formInputClass(formik.touched.name, formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phoneNumber" className={formLabelClass}>Mobile Number</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[16px] font-semibold text-[#0d0828]/50">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="10-digit number"
                    autoComplete="tel-national"
                    maxLength={10}
                    value={formik.values.phoneNumber}
                    onChange={(e) => formik.setFieldValue("phoneNumber", e.target.value.replace(/\D/g, "").slice(0, 10))}
                    onBlur={formik.handleBlur}
                    className={`${formInputClass(formik.touched.phoneNumber, formik.errors.phoneNumber)} pl-[52px]`}
                  />
                </div>
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-500">{formik.errors.phoneNumber}</p>
                )}
              </div>
            </div>

            {/* Row 3 — state, city, submit (3 across on desktop) */}
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:items-start">
              <div>
                <p className={formLabelClass}>State</p>
                <CustomSelect
                  value={formik.values.state}
                  onChange={(val) => {
                    formik.setFieldValue("state", val);
                    formik.setFieldValue("city", "");
                  }}
                  onBlur={() => formik.setFieldTouched("state", true)}
                  options={INDIA_STATES}
                  placeholder="Select state"
                  touched={formik.touched.state}
                  error={formik.errors.state}
                  triggerClassName={formInputClass(formik.touched.state, formik.errors.state)}
                  dropDirection="up"
                />
              </div>

              <div>
                <p className={formLabelClass}>City</p>
                <CustomSelect
                  value={formik.values.city}
                  onChange={(val) => formik.setFieldValue("city", val)}
                  onBlur={() => formik.setFieldTouched("city", true)}
                  options={CITIES_BY_STATE[formik.values.state] || []}
                  placeholder={formik.values.state ? "Select city" : "Select a state first"}
                  disabled={!formik.values.state}
                  touched={formik.touched.city}
                  error={formik.errors.city}
                  triggerClassName={formInputClass(formik.touched.city, formik.errors.city)}
                  dropDirection="up"
                />
              </div>


              <div className="mt-3 flex flex-col items-center gap-3 sm:col-span-2 lg:col-span-1 lg:mt-0">
                {/* Spacer so the button lines up with the inputs beside it */}
                <span aria-hidden="true" className={`${formLabelClass} hidden select-none lg:block`}>&nbsp;</span>
                <button
                  type="submit"
                  disabled={loading}
                  className={`-mt-3 flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-[#4328E7] py-4 lg:-mt-3 lg:max-w-none lg:rounded-2xl lg:py-[15px] text-base font-black text-white shadow-[0_18px_40px_-14px_rgba(67,40,231,0.9)] transition md:hover:-translate-y-0.5 md:hover:bg-[#3a20d0] ${
                    loading ? "cursor-not-allowed opacity-50 md:hover:translate-y-0 md:hover:bg-[#4328E7]" : ""
                  }`}
                >
                  {loading ? (
                    "Submitting..."
                  ) : (
                    <>
                      <Send className="h-5 w-5" /> Submit
                    </>
                  )}
                </button>
                <p className="text-center text-[12px] font-semibold text-[#0d0828]/40">
                  First Month EMI FREE* · 40+ banks &amp; lenders
                </p>
              </div>
            </div>
          </form>
          </>
          )}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* 1-minute process                                             */}
      {/* ---------------------------------------------------------- */}
      <section id="how-it-works" className="relative scroll-mt-4 overflow-hidden bg-[#f6f6fb] px-5 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-72 w-[680px] -translate-x-1/2 rounded-full bg-[#4328E7]/10 blur-3xl" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(#4328E7_0.8px,transparent_0.8px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_60%)]"
        />
        <div className="relative mx-auto max-w-7xl">
          {/* Heading */}
          <div className="text-center">
            <span className="inline-flex items-center rounded-full border border-[#4328E7]/15 bg-white px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#4328E7] shadow-sm">
              How it works
            </span>
            <h2 className="mt-4 text-3xl font-black tracking-[-0.02em] text-[#0d0828] sm:text-4xl lg:text-5xl">
              <span className="bg-gradient-to-r from-[#4328E7] to-[#7c6bff] bg-clip-text text-transparent">4</span>{" "}
              Simple Steps
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[15px] font-medium leading-6 text-[#0d0828]/60 sm:text-base">
              Any loan, any bank — First Month EMI FREE*, and rewards every month after that.
            </p>
          </div>

          {/* Step cards — stacked with a down-arrow between them on mobile,
              2×2 on tablet, four in a row with arrow connectors on xl+. */}
          <ol className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6 md:gap-y-10 lg:mt-16 xl:grid-cols-4 xl:gap-8">
            {LOAN_PROCESS_STEPS.map((step, i) => {
              const Icon = step.icon;
              const featured = Boolean(step.benefit);
              const isLast = i === LOAN_PROCESS_STEPS.length - 1;
              return (
                <li key={step.number} className="relative">
                  {featured && (
                    /* Ribbon that breaks out of the card edge (md+). On phones
                       the arrows between cards sit on the edges, so the
                       same pill is shown inside the card instead. */
                    <span className="absolute -top-4 right-5 z-20 hidden items-center gap-1.5 whitespace-nowrap rounded-full md:inline-flex bg-white px-4 py-2 text-[12px] font-black uppercase tracking-[0.08em] text-[#4328E7] shadow-[0_12px_26px_-12px_rgba(67,40,231,0.7)] ring-1 ring-[#4328E7]/20">
                      <Gift className="h-4 w-4" strokeWidth={2.75} />
                      {step.benefit}
                    </span>
                  )}
                  <div
                    className={`group flex h-full flex-col overflow-hidden rounded-[28px] transition duration-300 hover:-translate-y-1 ${
                      featured
                        ? "bg-gradient-to-br from-[#4328E7] via-[#5236f0] to-[#7b64ff] text-white shadow-[0_34px_70px_-30px_rgba(67,40,231,0.9)]"
                        : "border border-[#eceafb] bg-white text-[#0d0828] shadow-[0_24px_50px_-32px_rgba(13,8,40,0.45)] hover:shadow-[0_30px_60px_-30px_rgba(67,40,231,0.45)]"
                    }`}
                  >
                    {/* Copy */}
                    <div className="relative flex flex-1 flex-col px-6 pb-6 pt-6">
                      <div className="mb-3 flex items-end justify-between">
                        <p
                          className={`text-[12px] font-black uppercase tracking-[0.18em] ${
                            featured ? "text-white/70" : "text-[#4328E7]"
                          }`}
                        >
                          Step {step.number}
                        </p>
                        {/* Big faint step number */}
                        <span
                          aria-hidden="true"
                          className={`-mt-2 select-none text-5xl font-black leading-none tracking-[-0.04em] ${
                            featured ? "text-white/15" : "text-[#4328E7]/10"
                          }`}
                        >
                          {step.number}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
                            featured ? "bg-white/15 text-white" : "bg-[#EDE9FE] text-[#4328E7]"
                          }`}
                        >
                          <Icon className="h-[22px] w-[22px]" strokeWidth={2.25} />
                        </span>
                        <h3 className="text-xl font-black leading-tight tracking-[-0.01em] sm:text-[22px]">
                          {step.title}
                        </h3>
                      </div>
                      <p
                        className={`mt-3 text-[15px] leading-6 ${
                          featured ? "text-white/85" : "text-[#0d0828]/60"
                        }`}
                      >
                        {step.description}
                      </p>
                      {step.benefit && (
                        <span className="mt-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[12px] font-black uppercase tracking-[0.08em] text-[#4328E7] shadow-[0_12px_26px_-12px_rgba(0,0,0,0.5)] md:hidden">
                          <Gift className="h-4 w-4" strokeWidth={2.75} />
                          {step.benefit}
                        </span>
                      )}
                    </div>

                    {/* Illustration panel — sits at the bottom; copy above takes the
                        flexible space so every card's panel lines up. */}
                    <div
                      className={`relative flex h-[280px] items-center justify-center px-4 ${
                        featured ? "bg-white/10" : step.panel
                      }`}
                    >
                      <div className="transition duration-300 group-hover:scale-[1.03]">
                        <StepVisual kind={step.visual} />
                      </div>
                    </div>
                  </div>

                  {!isLast && (
                    <>
                      {/* Desktop arrow → next card */}
                      <span
                        aria-hidden="true"
                        className="absolute -right-[26px] bottom-[140px] z-10 hidden h-11 w-11 translate-y-1/2 items-center justify-center rounded-full bg-white text-[#4328E7] shadow-[0_10px_25px_-10px_rgba(67,40,231,0.6)] ring-4 ring-[#f6f6fb] xl:flex"
                      >
                        <ArrowRight className="h-5 w-5" strokeWidth={2.75} />
                      </span>
                      {/* Mobile arrow ↓ next card */}
                      <span aria-hidden="true" className="relative z-10 -mb-7 mt-1 flex justify-center md:hidden">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#4328E7] shadow-[0_10px_25px_-10px_rgba(67,40,231,0.6)] ring-4 ring-[#f6f6fb]">
                          <ArrowDown className="h-5 w-5" strokeWidth={2.75} />
                        </span>
                      </span>
                    </>
                  )}
                </li>
              );
            })}
          </ol>

          {/* Benefit banner — the one thing to remember from this section.
              Same lavender/purple language as the rest of the page. */}
          <div className="relative mt-14 overflow-hidden rounded-[28px] border border-[#4328E7]/15 bg-gradient-to-r from-white via-[#f7f5ff] to-[#efebff] shadow-[0_24px_50px_-34px_rgba(67,40,231,0.55)]">
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-[#4328E7]/10 blur-3xl" />
            <div className="relative flex flex-col items-center gap-6 px-6 py-7 text-center md:flex-row md:gap-8 md:px-10 md:text-left">
              {/* FREE* seal */}
              <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#4328E7] to-[#7b64ff] text-white shadow-[0_16px_30px_-14px_rgba(67,40,231,0.9)] ring-[6px] ring-white md:h-[104px] md:w-[104px]">
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/75">1st EMI</span>
                <span className="text-2xl font-black leading-none md:text-3xl">FREE*</span>
              </div>

              <div className="flex-1">
                <p className="text-[12px] font-black uppercase tracking-[0.18em] text-[#4328E7]">
                  On every loan · From any bank
                </p>
                <h3 className="mt-1.5 text-2xl font-black leading-tight tracking-[-0.02em] text-[#0d0828] sm:text-3xl">
                  First Month{" "}
                  <span className="bg-gradient-to-r from-[#4328E7] to-[#7c6bff] bg-clip-text text-transparent">EMI FREE*</span>
                </h3>
                <p className="mt-1.5 text-[15px] font-medium leading-6 text-[#0d0828]/60">
                  Choose from 40+ banks &amp; lenders — your first month&apos;s EMI is on us.
                </p>
              </div>

              <a
                href="#apply"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#4328E7] px-8 py-4 text-base font-black text-white shadow-[0_18px_40px_-14px_rgba(67,40,231,0.9)] transition hover:-translate-y-0.5 hover:bg-[#3a20d0]"
              >
                Get Started
                <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" strokeWidth={2.5} />
              </a>
            </div>
          </div>
          <p className="mt-3 text-center text-xs font-medium text-[#0d0828]/45">*T&amp;C apply</p>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Credit life cycle — the tools beyond the first loan. Top-Up
          Personal Loan and RateSwitch are the featured highlight cards;
          AI Liability Advisor, Home Finance OS and Vehicle Financial OS
          are compact tools below them; Debt Optimizer and Liability
          Health close the section as a 50/50 pair. */}
      {/* ---------------------------------------------------------- */}
      <section id="credit-life-cycle" className="relative overflow-hidden bg-white px-5 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-[#4328E7]/[0.06] blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -right-40 bottom-10 h-96 w-96 rounded-full bg-[#7c6bff]/[0.07] blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          <div className="text-center">
            <span className="inline-flex items-center rounded-full border border-[#4328E7]/15 bg-white px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#4328E7] shadow-sm">
              Beyond your loan
            </span>
            <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-black leading-[1.15] tracking-[-0.02em] text-[#0d0828] sm:text-4xl lg:text-5xl">
              We manage your{" "}
              <span className="bg-gradient-to-r from-[#4328E7] to-[#7c6bff] bg-clip-text text-transparent">
                credit life cycle
              </span>
            </h2>
          </div>

          {/* Featured: Top-Up Personal Loan + RateSwitch */}
          <div className="mt-12 grid grid-cols-1 gap-5 lg:mt-16 lg:grid-cols-2">
            {/* Top-Up Personal Loan */}
            <div className="relative flex flex-col overflow-hidden rounded-[24px] bg-gradient-to-br from-[#4328E7] via-[#5236f0] to-[#7b64ff] p-5 text-white shadow-[0_28px_56px_-28px_rgba(67,40,231,0.9)] sm:p-6">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                  <Wallet className="h-5 w-5" strokeWidth={2.25} />
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-white/90">
                  {CREDIT_LIFECYCLE_TOPUP.badge}
                </span>
              </div>
              <h3 className="relative mt-4 text-xl font-black tracking-[-0.01em] sm:text-2xl">{CREDIT_LIFECYCLE_TOPUP.title}</h3>
              <p className="relative mt-1.5 max-w-md text-[13.5px] leading-6 text-white/85 sm:text-[15px]">
                {CREDIT_LIFECYCLE_TOPUP.description}
              </p>
              <div className="relative mt-4 flex flex-wrap gap-2">
                {CREDIT_LIFECYCLE_TOPUP.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-bold text-white ring-1 ring-white/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Unlock mock */}
              <div className="relative mt-5 flex flex-1 items-end">
                <div className="w-full rounded-xl bg-white p-3.5 text-[#0d0828] shadow-[0_16px_32px_-18px_rgba(13,8,40,0.6)] sm:p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Existing loan</p>
                      <p className="mt-1 text-base font-black text-[#0d0828]">Active</p>
                    </div>
                    <ArrowRight className="h-5 w-5 flex-none text-[#0d0828]/25" strokeWidth={2.5} />
                    <div className="text-right">
                      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4328E7]">Top-up unlocked</p>
                      <p className="mt-1 text-base font-black text-[#4328E7]">Available</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 rounded-xl bg-[#f3f1ff] px-3.5 py-2.5 text-[12.5px] font-bold text-[#4328E7]">
                    <Percent className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Potentially lower rate than a fresh loan
                  </div>
                </div>
              </div>
            </div>

            {/* RateSwitch */}
            <div className="relative flex flex-col overflow-hidden rounded-[24px] bg-gradient-to-br from-[#4328E7] via-[#5236f0] to-[#7b64ff] p-5 text-white shadow-[0_28px_56px_-28px_rgba(67,40,231,0.9)] sm:p-6">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                  <ArrowLeftRight className="h-5 w-5" strokeWidth={2.25} />
                </span>
                <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-white/90">
                  {CREDIT_LIFECYCLE_RATESWITCH.badge}
                </span>
              </div>
              <h3 className="relative mt-4 text-xl font-black tracking-[-0.01em] sm:text-2xl">{CREDIT_LIFECYCLE_RATESWITCH.title}</h3>
              <p className="relative mt-1.5 max-w-md text-[13.5px] leading-6 text-white/85 sm:text-[15px]">
                {CREDIT_LIFECYCLE_RATESWITCH.description}
              </p>
              <div className="relative mt-4 flex flex-wrap gap-2">
                {CREDIT_LIFECYCLE_RATESWITCH.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-white/10 px-3 py-1.5 text-[12px] font-bold text-white ring-1 ring-white/20"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Rate comparison mock */}
              <div className="relative mt-5 flex flex-1 items-end">
                <div className="w-full rounded-xl bg-white p-3.5 text-[#0d0828] shadow-[0_16px_32px_-18px_rgba(13,8,40,0.6)] sm:p-4">
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-[11px] font-bold text-[#0d0828]/50">
                        <span>Current rate</span>
                      </div>
                      <div className="mt-1.5 h-2.5 w-full rounded-full bg-[#0d0828]/10">
                        <div className="h-2.5 w-[85%] rounded-full bg-[#0d0828]/25" />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-[11px] font-bold text-[#4328E7]">
                        <span>Switch rate</span>
                      </div>
                      <div className="mt-1.5 h-2.5 w-full rounded-full bg-[#f3f1ff]">
                        <div className="h-2.5 w-[55%] rounded-full bg-gradient-to-r from-[#4328E7] to-[#7b64ff]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact tools */}
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CREDIT_LIFECYCLE_TOOLS.map(({ title, description, icon: Icon, tags }) => (
              <div
                key={title}
                className="group relative flex flex-col rounded-[24px] border border-[#eceafb] bg-white p-6 shadow-[0_20px_45px_-34px_rgba(13,8,40,0.45)] transition duration-300 hover:-translate-y-1 hover:border-[#4328E7]/25 hover:shadow-[0_28px_55px_-30px_rgba(67,40,231,0.4)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EDE9FE] text-[#4328E7] transition group-hover:bg-[#4328E7] group-hover:text-white">
                  <Icon className="h-6 w-6" strokeWidth={2.25} />
                </span>
                <h3 className="mt-5 text-lg font-black leading-tight tracking-[-0.01em] text-[#0d0828]">{title}</h3>
                <p className="mt-2 text-[14px] leading-6 text-[#0d0828]/60">{description}</p>
                <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
                  {tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-[#f6f5ff] px-2.5 py-1 text-[11px] font-bold text-[#0d0828]/60">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Debt Optimizer + Liability Health */}
          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Debt Optimizer */}
            <div className="relative flex flex-col gap-7 overflow-hidden rounded-[28px] border border-[#4328E7]/15 bg-gradient-to-r from-white via-[#f7f5ff] to-[#efebff] p-6 sm:p-8 lg:flex-row lg:items-center lg:gap-8">
              <div className="lg:w-[38%]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#4328E7] text-white shadow-[0_12px_24px_-10px_rgba(67,40,231,0.8)]">
                  <SlidersHorizontal className="h-6 w-6" strokeWidth={2.25} />
                </span>
                <h3 className="mt-5 text-2xl font-black tracking-[-0.01em] text-[#0d0828]">{CREDIT_LIFECYCLE_DEBT_OPTIMIZER.title}</h3>
                <p className="mt-2 text-[15px] leading-6 text-[#0d0828]/60">{CREDIT_LIFECYCLE_DEBT_OPTIMIZER.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {CREDIT_LIFECYCLE_DEBT_OPTIMIZER.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#0d0828]/60 ring-1 ring-[#eceafb]">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Optimization scan mock */}
              <div className="flex-1 rounded-2xl bg-white p-5 shadow-[0_20px_45px_-30px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb] sm:p-6">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Optimization scan</p>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E6FAF3] px-2.5 py-1 text-[11px] font-black text-[#0f9f73]">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
                    3 found
                  </span>
                </div>
                <div className="mt-5 space-y-2.5">
                  {[
                    { label: "Lower interest rate", icon: Percent },
                    { label: "Shorter tenure option", icon: SlidersHorizontal },
                    { label: "Prepayment savings", icon: IndianRupee },
                  ].map(({ label, icon: Icon }) => (
                    <div key={label} className="flex items-center justify-between rounded-xl bg-[#fafafe] px-3 py-2.5 ring-1 ring-[#eceafb]">
                      <span className="flex items-center gap-2 text-[12.5px] font-bold text-[#0d0828]/70">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#EDE9FE] text-[#4328E7]">
                          <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </span>
                        {label}
                      </span>
                      <ArrowRight className="h-4 w-4 text-[#0d0828]/30" strokeWidth={2.5} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Liability Health */}
            <div className="relative flex flex-col gap-7 overflow-hidden rounded-[28px] border border-[#4328E7]/15 bg-gradient-to-r from-white via-[#f7f5ff] to-[#efebff] p-6 sm:p-8 lg:flex-row lg:items-center lg:gap-8">
              <div className="lg:w-[38%]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#4328E7] text-white shadow-[0_12px_24px_-10px_rgba(67,40,231,0.8)]">
                  <HeartPulse className="h-6 w-6" strokeWidth={2.25} />
                </span>
                <h3 className="mt-5 text-2xl font-black tracking-[-0.01em] text-[#0d0828]">Liability Health</h3>
                <p className="mt-2 text-[15px] leading-6 text-[#0d0828]/60">{CREDIT_LIFECYCLE_HEALTH.description}</p>
              </div>

              {/* Health meter mock */}
              <div className="flex-1 rounded-2xl bg-white p-5 shadow-[0_20px_45px_-30px_rgba(13,8,40,0.45)] ring-1 ring-[#eceafb] sm:p-6">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#0d0828]/40">Your debt position</p>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E6FAF3] px-2.5 py-1 text-[11px] font-black text-[#0f9f73]">
                    <Activity className="h-3.5 w-3.5" strokeWidth={2.75} />
                    Tracking
                  </span>
                </div>
                <div className="relative mt-6">
                  <div className="h-3 rounded-full bg-gradient-to-r from-[#c9c0ff] via-[#8a73ff] to-[#4328E7]" />
                  <span
                    aria-hidden="true"
                    className="absolute left-[68%] top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white bg-[#4328E7] shadow-[0_6px_14px_-4px_rgba(67,40,231,0.9)]"
                  />
                </div>
                <div className="mt-2.5 grid grid-cols-3 text-[11px] font-bold text-[#0d0828]/45">
                  <span>Needs attention</span>
                  <span className="text-center">Fair</span>
                  <span className="text-right">Healthy</span>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3 border-t border-[#eceafb] pt-5">
                  {["Active loans", "Monthly EMIs", "Repayments"].map((label) => (
                    <div key={label}>
                      <p className="text-[11px] font-bold text-[#0d0828]/45">{label}</p>
                      <span className="mt-2 block h-2 w-3/4 rounded-full bg-[#0d0828]/[0.12]" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Happy customers — carousel of real customer testimonials.    */}
      {/* ---------------------------------------------------------- */}
      <section className="bg-white px-5 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-5xl text-center">
          <h2 className="text-2xl font-black tracking-[-0.02em] text-[#0d0828] sm:text-3xl">
            Happy customers
          </h2>
        </div>

        {/* Mobile: one card on screen at a time — swipe manually, or it
            auto-advances every few seconds on its own. */}
        <div
          ref={testimonialScrollRef}
          className="car-loan-testimonial-scroll -mx-5 mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-1 sm:hidden"
        >
          {CUSTOMER_TESTIMONIALS.map((t, index) => (
            <div
              key={t.id}
              ref={(el) => (testimonialCardRefs.current[index] = el)}
              className="relative w-[85%] shrink-0 snap-center overflow-hidden rounded-2xl border border-[#eceafb]"
              style={{ aspectRatio: "4 / 5" }}
            >
              <TestimonialCard testimonial={t} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-1.5 sm:hidden">
          {CUSTOMER_TESTIMONIALS.map((_, index) => (
            <span
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === activeTestimonialIndex ? "w-5 bg-[#4328E7]" : "w-1.5 bg-[#0d0828]/15"
              }`}
            />
          ))}
        </div>

        {/* sm and up: continuous auto-scrolling marquee. */}
        <div
          className="relative mt-10 hidden overflow-hidden sm:block"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)",
          }}
        >
          <div className="car-loan-testimonial-track flex w-max items-stretch gap-6">
            {[...CUSTOMER_TESTIMONIALS, ...CUSTOMER_TESTIMONIALS].map((t, i) => (
              <div
                key={`${t.id}-${i}`}
                className="relative w-[280px] shrink-0 overflow-hidden rounded-2xl border border-[#eceafb] sm:w-[320px]"
                style={{ aspectRatio: "4 / 5" }}
              >
                <TestimonialCard testimonial={t} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* FAQ                                                          */}
      {/* ---------------------------------------------------------- */}
      <section className="bg-[#faf9ff] px-5 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-2xl font-black tracking-[-0.02em] sm:text-3xl">
            Frequently asked questions
          </h2>

          <div className="mt-10 overflow-hidden rounded-[26px] border border-[#eceafb] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03),0_24px_60px_-30px_rgba(0,0,0,0.09)]">
            {LOAN_FAQS.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              const panelId = `car-loan-faq-panel-${index}`;
              const buttonId = `car-loan-faq-button-${index}`;
              return (
                <div key={faq.question} className="border-b border-[#f0eefb] bg-white last:border-b-0">
                  <h3 className="m-0">
                    <button
                      id={buttonId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left sm:px-8"
                    >
                      <span className="text-[15px] font-bold tracking-[-0.015em] text-[#0d0828] sm:text-[17px]">
                        {faq.question}
                      </span>
                      <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-full border transition duration-300 ${
                          isOpen
                            ? "rotate-45 border-transparent bg-[#4328E7] text-white"
                            : "border-[#e6e3f7] bg-white text-[#4328E7]"
                        }`}
                      >
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                      </span>
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className={`grid transition-all duration-300 ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="space-y-3 px-6 pb-6 sm:px-8">
                        {faq.blocks ? (
                          faq.blocks.map((block, blockIndex) =>
                            block.t === "ul" ? (
                              <ul key={blockIndex} className="space-y-1.5">
                                {block.c.map((item) => (
                                  <li
                                    key={item}
                                    className="flex gap-2 text-sm leading-7 text-[#0d0828b3] sm:text-base"
                                  >
                                    <span className="mt-[11px] h-1.5 w-1.5 flex-none rounded-full bg-[#4328E7]/60" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p key={blockIndex} className="text-sm leading-7 text-[#0d0828b3] sm:text-base">
                                {block.c}
                              </p>
                            )
                          )
                        ) : (
                          <p className="text-sm leading-7 text-[#0d0828b3] sm:text-base">{faq.answer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-center text-xs font-medium text-[#0d0828]/45">*T&amp;C apply</p>
        </div>
      </section>

      {/* ---------------------------------------------------------- */}
      {/* Final CTA — download the app                                 */}
      {/* ---------------------------------------------------------- */}
      <section className="bg-[#faf9ff] px-5 pb-16 sm:px-6 lg:px-8 lg:pb-24">
        <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-gradient-to-br from-[#4328E7] via-[#5236f0] to-[#7b64ff] px-6 py-12 text-center text-white shadow-[0_40px_80px_-40px_rgba(67,40,231,0.9)] sm:px-10 sm:py-14 lg:px-16 lg:py-16 lg:text-left">
          {/* Decorative glow + dot grid */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/15 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-[#8a73ff]/40 blur-3xl" />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-20 [background-image:radial-gradient(#ffffff_0.8px,transparent_0.8px)] [background-size:22px_22px] [mask-image:linear-gradient(90deg,transparent,black_60%)]"
          />

          <div className="relative grid items-center gap-12 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h2 className="text-4xl font-black leading-[1.02] tracking-[-0.03em] sm:text-5xl lg:text-6xl">
                First Month
                <br />
                EMI FREE<sup className="ml-0.5 align-super text-[0.45em]">*</sup>
              </h2>
              <p className="mt-4 text-lg font-semibold text-white/85 sm:text-xl">Download P2B app today</p>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                <a href={APPSTORE_URL} target="_blank" rel="noopener noreferrer" className="rounded-[10px] ring-1 ring-white/30 transition duration-300 hover:-translate-y-0.5">
                  <img src={appStoreBadge} alt="Download on the App Store" className="h-[48px] w-auto" />
                </a>
                <a href={PLAYSTORE_URL} target="_blank" rel="noopener noreferrer" className="rounded-[10px] ring-1 ring-white/30 transition duration-300 hover:-translate-y-0.5">
                  <img src={playStoreBadge} alt="Get it on Google Play" className="h-[48px] w-auto" />
                </a>
              </div>
              <p className="mt-5 text-xs font-medium text-white/60">*T&amp;C apply</p>
            </div>

            {/* Two app screens in phone frames */}
            <div aria-hidden="true" className="relative mx-auto h-[400px] w-full max-w-[380px] sm:h-[460px] lg:h-[480px] lg:max-w-[440px]">
              {[
                { src: appScreenBenefits, cls: "left-0 top-6 -rotate-6 scale-[0.92] opacity-95" },
                { src: appScreenLoans, cls: "right-0 top-0 rotate-3 z-10" },
              ].map(({ src, cls }) => (
                <div
                  key={src}
                  className={`absolute w-[52%] rounded-[34px] bg-[#0d0828] p-[7px] shadow-[0_30px_60px_-20px_rgba(13,8,40,0.7)] ring-1 ring-white/20 ${cls}`}
                >
                  <div className="relative aspect-[9/19] overflow-hidden rounded-[28px] bg-white">
                    {/* Dynamic-island style notch */}
                    <span className="absolute left-1/2 top-2 z-10 h-[14px] w-[34%] -translate-x-1/2 rounded-full bg-[#0d0828]" />
                    <img src={src} alt="" loading="lazy" className="h-full w-full object-cover object-top pt-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
